import type { CSSProperties } from 'react';

import { cx } from '@/lib/cx';

import styles from './Skeleton.module.css';

export type SkeletonProps = {
  width?: number | string;
  height?: number | string;
  radius?: string;
  className?: string;
};

export function Skeleton({ width = '100%', height = 12, radius, className }: SkeletonProps) {
  const style: CSSProperties = {
    inlineSize: width,
    blockSize: height,
    ...(radius ? { borderRadius: radius } : {}),
  };
  return <span className={cx(styles.skeleton, className)} style={style} aria-hidden="true" />;
}

export type SkeletonTableProps = {
  rows?: number;
  columns?: number;
  label?: string;
};

/**
 * Placeholder rows that match the shape of the table they stand in for.
 *
 * Skeleton rows, not a spinner over the page (plan 9.4): the layout does not jump when
 * the data lands, and the eye already knows where to look.
 *
 * One live region announces the wait, rather than every placeholder cell announcing
 * itself.
 */
export function SkeletonTable({
  rows = 6,
  columns = 5,
  label = 'Loading',
}: SkeletonTableProps) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div className={styles.row} key={rowIndex}>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              // Varying widths so it reads as text rather than as a loading bar.
              width={columnIndex === 0 ? '22%' : `${14 + ((columnIndex * 7) % 12)}%`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
